(function (globalScope, factory) {
  const api = factory();

  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  }

  if (globalScope && globalScope.document) {
    globalScope.MnDelivery = api;
    api.init(globalScope.document, globalScope);
  }
})(typeof window !== 'undefined' ? window : globalThis, function () {
  const CEP_KEY = 'maknorte_cep';
  const CEP_SAVED_AT_KEY = 'maknorte_cep_saved_at';
  const CEP_RETENTION_MS = 30 * 864e5;
  let preferencesState = 'unknown';
  let preferencesReady = false;
  let sessionCep = '';
  let privacyDocument = null;
  let privacyWindow = null;

  function cleanCep(value) {
    return String(value || '').replace(/\D/g, '').slice(0, 9);
  }

  function formatCep(value) {
    const digits = cleanCep(value).slice(0, 8);
    return digits.length > 5 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : digits;
  }

  function isCepFormat(value) {
    return /^\d{8}$/.test(cleanCep(value));
  }

  function storeCep(value, storage) {
    const digits = cleanCep(value);
    if (!isCepFormat(digits)) {
      return { ok: false, error: 'invalid_format' };
    }

    const formatted = formatCep(digits);
    const permission = refreshPreferences(false);
    sessionCep = formatted;
    if (permission === 'allowed') {
      try {
        if (storage) {
          storage.setItem(CEP_SAVED_AT_KEY, String(Date.now()));
          storage.setItem(CEP_KEY, formatted);
        }
      } catch { clearPersistedCep(privacyDocument, privacyWindow); }
    }
    return { ok: true, digits, formatted, verification: 'format_only' };
  }

  function checkoutNotice(context) {
    return {
      title: 'Frete e prazo calculados no checkout',
      context: 'O valor e o prazo dependem do endereço e dos itens do pedido.',
      quoted: false,
      cepVerification: isCepFormat(context.cep) ? 'format_only' : 'not_provided'
    };
  }

  function readStoredCep(storage) {
    if (refreshPreferences(false) !== 'allowed') return '';
    try {
      const value = storage && storage.getItem(CEP_KEY);
      if (!isCepFormat(value)) return '';
      const savedAtValue = storage.getItem(CEP_SAVED_AT_KEY);
      if (!savedAtValue) {
        storage.setItem(CEP_SAVED_AT_KEY, String(Date.now()));
        return formatCep(value);
      }
      const savedAt = Number(savedAtValue);
      if (!Number.isFinite(savedAt) || savedAt <= 0 || Date.now() - savedAt > CEP_RETENTION_MS || savedAt > Date.now()) {
        clearPersistedCep(privacyDocument, privacyWindow);
        return '';
      }
      return formatCep(value);
    } catch {
      return '';
    }
  }

  function availableCep(storage) {
    return sessionCep || readStoredCep(storage);
  }

  function getCep() {
    return availableCep(localStorageFor(privacyWindow));
  }

  function setCep(value, source = 'manual') {
    const result = storeCep(value, localStorageFor(privacyWindow));
    if (!result.ok) return result;
    const detail = { cep: result.formatted, verification: result.verification, source };
    if (privacyWindow && typeof privacyWindow.dispatchEvent === 'function' && typeof privacyWindow.CustomEvent === 'function') {
      privacyWindow.dispatchEvent(new privacyWindow.CustomEvent('maknorte:cep:changed', { detail }));
    }
    return Object.assign({}, result, { source });
  }

  function clearPersistedCep(documentRoot, windowRoot) {
    const storage = localStorageFor(windowRoot);
    try { if (storage) { storage.removeItem(CEP_KEY); storage.removeItem(CEP_SAVED_AT_KEY); } } catch { /* noop */ }
    try { if (documentRoot) documentRoot.cookie = `${CEP_KEY}=;path=/;max-age=0;SameSite=Lax;Secure`; } catch { /* noop */ }
  }

  function refreshPreferences(confirmed) {
    const previous = preferencesState;
    if (confirmed) preferencesReady = true;
    if (!preferencesReady) {
      preferencesState = 'unknown';
      return preferencesState;
    }
    let next = 'unknown';
    try {
      const privacy = privacyWindow && privacyWindow.Shopify && privacyWindow.Shopify.customerPrivacy;
      if (privacy && typeof privacy.preferencesProcessingAllowed === 'function') {
        const allowed = privacy.preferencesProcessingAllowed();
        if (allowed === true) next = 'allowed';
        else if (allowed === false) next = 'denied';
      }
    } catch { next = 'unknown'; }
    preferencesState = next;
    if (next === 'denied' && previous !== 'denied' && privacyDocument && privacyWindow) {
      clearPersistedCep(privacyDocument, privacyWindow);
      if (previous === 'allowed') sessionCep = '';
    } else if (next === 'allowed' && previous !== 'allowed' && privacyDocument) {
      try { privacyDocument.cookie = `${CEP_KEY}=;path=/;max-age=0;SameSite=Lax;Secure`; } catch { /* noop */ }
    }
    return preferencesState;
  }

  function localStorageFor(windowRoot) {
    try {
      return windowRoot.localStorage;
    } catch {
      return null;
    }
  }

  function renderForm(form, windowRoot, syncInput = true) {
    const input = form.querySelector('[data-mn-cep-input]');
    const deliveryRoot = form.closest('[data-mn-delivery]') || form.parentElement;
    const title = deliveryRoot.querySelector('[data-mn-delivery-title]');
    const contextElement = deliveryRoot.querySelector('[data-mn-delivery-context]');
    const savedElement = deliveryRoot.querySelector('[data-mn-delivery-saved]');
    const stored = availableCep(localStorageFor(windowRoot));
    const canPersist = refreshPreferences(false) === 'allowed';
    const notice = checkoutNotice({ cep: stored });

    if (input && syncInput && input.value !== stored) input.value = stored;
    else if (input && !syncInput && !input.value && stored) input.value = stored;
    if (title) title.textContent = notice.title;
    if (contextElement) contextElement.textContent = notice.context;
    if (savedElement) {
      savedElement.textContent = stored
        ? (canPersist
          ? `CEP ${stored} salvo neste navegador. O endereço ainda não foi validado ou cotado.`
          : `CEP ${stored} em uso nesta página. O endereço ainda não foi validado ou cotado.`)
        : (canPersist
          ? 'Você pode salvar um CEP neste navegador. A cotação não é feita nesta página.'
          : 'Informe um CEP para usar nesta página. A cotação não é feita aqui.');
    }
  }

  function renderBanner(documentRoot, windowRoot) {
    const banner = documentRoot.querySelector('[data-mn-cep-banner]');
    if (!banner) return;
    const cep = availableCep(localStorageFor(windowRoot));
    const value = banner.querySelector('[data-mn-banner-cep]');
    const focusButton = banner.querySelector('[data-mn-focus-cep]');
    banner.hidden = false;
    const label = banner.querySelector('[data-mn-banner-label]');
    if (label && label.firstChild) label.firstChild.textContent = cep ? 'CEP ' : 'Consulte a entrega ';
    if (value) value.textContent = cep;
    if (focusButton) focusButton.hidden = !(
      documentRoot.querySelector('[data-mn-cep-input]')
      || documentRoot.querySelector('[data-mv2-cep] input')
      || documentRoot.querySelector('[data-mv2-ship-cep]')
      || documentRoot.querySelector('[data-mn-cep-dialog]')
    );
  }

  function openCepDialog(documentRoot, windowRoot) {
    const dialog = documentRoot.querySelector('[data-mn-cep-dialog]');
    if (!dialog) return false;
    const input = dialog.querySelector('[data-mn-cep-popup-input]');
    if (!input) return false;
    const stored = availableCep(localStorageFor(windowRoot));
    if (input.value !== stored) input.value = stored;
    try {
      if (typeof dialog.showModal === 'function' && !dialog.open) dialog.showModal();
      else {
        dialog.open = true;
        dialog.hidden = false;
      }
    } catch {
      dialog.open = true;
      dialog.hidden = false;
    }
    input.focus({ preventScroll: true });
    return true;
  }

  function injectCards(documentRoot) {
    const template = documentRoot.querySelector('[data-mn-card-delivery-template]');
    if (!template || !template.content) return;

    documentRoot.querySelectorAll('product-card').forEach((card) => {
      if (card.querySelector('[data-mn-card-delivery]')) return;
      const node = template.content.firstElementChild.cloneNode(true);
      const pricing = card.querySelector('.mak-pricing-box');
      const content = card.querySelector('.product-card__content');
      if (pricing && pricing.parentNode) pricing.parentNode.insertBefore(node, pricing);
      else if (content) content.appendChild(node);
      else card.appendChild(node);
    });
  }

  function focusInlineInput(documentRoot) {
    const inputs = [
      ...Array.from(documentRoot.querySelectorAll('[data-mn-cep-input]')),
      ...Array.from(documentRoot.querySelectorAll('[data-mv2-cep] input')),
      ...Array.from(documentRoot.querySelectorAll('[data-mv2-ship-cep]')),
    ];
    const input = inputs.find((candidate) => candidate.offsetParent !== null) || inputs[0];
    if (!input) return openCepDialog(documentRoot, privacyWindow);
    const v2Box = input.closest('[data-mv2-cep]');
    if (v2Box) {
      const toggle = v2Box.querySelector('[data-mv2-cep-toggle]');
      const form = v2Box.querySelector('form');
      if (toggle) toggle.setAttribute('aria-expanded', 'true');
      if (form) form.hidden = false;
    }
    const disclosure = input.closest('details');
    if (disclosure) disclosure.open = true;
    input.focus({ preventScroll: true });
    input.scrollIntoView({ behavior: 'smooth', block: 'center' });
    return true;
  }

  function init(documentRoot, windowRoot) {
    if (documentRoot.documentElement.dataset.mnDeliveryReady === 'true') return;
    documentRoot.documentElement.dataset.mnDeliveryReady = 'true';
    privacyDocument = documentRoot;
    privacyWindow = windowRoot;

    const forms = documentRoot.querySelectorAll('[data-mn-delivery-form]');
    const popupDialog = documentRoot.querySelector('[data-mn-cep-dialog]');
    const popupForm = popupDialog && popupDialog.querySelector('[data-mn-cep-popup-form]');
    const popupInput = popupDialog && popupDialog.querySelector('[data-mn-cep-popup-input]');
    const popupError = popupDialog && popupDialog.querySelector('[data-mn-cep-popup-error]');
    const renderPopup = (syncInput = true) => {
      if (!popupInput) return;
      const stored = availableCep(localStorageFor(windowRoot));
      if (syncInput && popupInput.value !== stored) popupInput.value = stored;
      else if (!syncInput && !popupInput.value && stored) popupInput.value = stored;
    };
    const renderAll = (syncInputs = true) => {
      forms.forEach((form) => renderForm(form, windowRoot, syncInputs));
      renderPopup(syncInputs);
      renderBanner(documentRoot, windowRoot);
    };
    documentRoot.addEventListener('visitorConsentCollected', () => {
      const previous = preferencesState;
      const next = refreshPreferences(true);
      if (next === 'denied' && previous === 'allowed') {
        if (privacyWindow && typeof privacyWindow.dispatchEvent === 'function' && typeof privacyWindow.CustomEvent === 'function') {
          privacyWindow.dispatchEvent(new privacyWindow.CustomEvent('maknorte:cep:changed', {
            detail: { cep: '', verification: 'not_provided', source: 'consent_revoked' }
          }));
        }
        return;
      }
      renderAll(false);
      if (next === 'allowed' && privacyWindow && typeof privacyWindow.dispatchEvent === 'function' && typeof privacyWindow.CustomEvent === 'function') {
        privacyWindow.dispatchEvent(new privacyWindow.CustomEvent('maknorte:cep:changed', {
          detail: { cep: getCep(), verification: getCep() ? 'format_only' : 'not_provided', source: 'consent_ready' }
        }));
      }
    });
    try {
      const shopify = windowRoot.Shopify;
      if (shopify && typeof shopify.loadFeatures === 'function') {
        shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
          if (error) { preferencesReady = false; preferencesState = 'unknown'; renderAll(false); return; }
          refreshPreferences(true);
          renderAll(false);
          if (preferencesState === 'allowed' && privacyWindow && typeof privacyWindow.dispatchEvent === 'function' && typeof privacyWindow.CustomEvent === 'function') {
            const cep = getCep();
            privacyWindow.dispatchEvent(new privacyWindow.CustomEvent('maknorte:cep:changed', {
              detail: { cep, verification: cep ? 'format_only' : 'not_provided', source: 'consent_ready' }
            }));
          }
        });
      }
    } catch { preferencesReady = false; preferencesState = 'unknown'; }

    forms.forEach((form) => {
      const input = form.querySelector('[data-mn-cep-input]');
      const error = form.querySelector('[data-mn-cep-error]');

      input.addEventListener('input', () => {
        input.value = formatCep(input.value);
        input.removeAttribute('aria-invalid');
        if (error) error.textContent = '';
      });

      form.addEventListener('submit', (event) => {
        event.preventDefault();
        const result = setCep(input.value, 'legacy');
        if (!result.ok) {
          input.setAttribute('aria-invalid', 'true');
          if (error) {
            error.textContent = result.error === 'invalid_format'
              ? 'Informe um CEP com 8 dígitos.'
              : 'Não foi possível salvar o CEP neste navegador. Você pode continuar e calcular no checkout.';
          }
          input.focus();
          return;
        }

        if (error) error.textContent = '';
        renderForm(form, windowRoot);
        renderBanner(documentRoot, windowRoot);
      });

      renderForm(form, windowRoot);
    });

    if (popupInput) {
      popupInput.addEventListener('input', () => {
        popupInput.value = formatCep(popupInput.value);
        popupInput.removeAttribute('aria-invalid');
        if (popupError) popupError.textContent = '';
      });
      renderPopup();
    }

    if (popupForm && popupInput) {
      popupForm.addEventListener('submit', (event) => {
        event.preventDefault();
        const result = setCep(popupInput.value, 'dialog');
        if (!result.ok) {
          popupInput.setAttribute('aria-invalid', 'true');
          if (popupError) popupError.textContent = 'Informe um CEP com 8 dígitos.';
          popupInput.focus();
          return;
        }
        if (popupError) popupError.textContent = '';
        if (popupDialog) {
          try {
            if (typeof popupDialog.close === 'function') popupDialog.close();
            else { popupDialog.open = false; popupDialog.hidden = true; }
          } catch { popupDialog.open = false; popupDialog.hidden = true; }
        }
        renderAll();
      });
    }

    documentRoot.addEventListener('click', (event) => {
      const focusButton = event.target.closest('[data-mn-focus-cep]');
      if (focusButton) focusInlineInput(documentRoot);
      const closeButton = event.target.closest('[data-mn-cep-popup-close]');
      if (closeButton && popupDialog) {
        try {
          if (typeof popupDialog.close === 'function') popupDialog.close();
          else { popupDialog.open = false; popupDialog.hidden = true; }
        } catch { popupDialog.open = false; popupDialog.hidden = true; }
      }
    });

    windowRoot.makOpenCepPopup = () => focusInlineInput(documentRoot);
    windowRoot.addEventListener('maknorte:cep:changed', (event) => {
      renderAll(event?.detail?.source !== 'consent_ready');
    });
    windowRoot.addEventListener('storage', (event) => {
      if (refreshPreferences(false) !== 'allowed') return;
      if (event.key !== CEP_KEY && event.key !== CEP_SAVED_AT_KEY) return;
      try {
        const storage = localStorageFor(windowRoot);
        const value = event.key === CEP_KEY ? event.newValue : (storage && storage.getItem(CEP_KEY));
        const savedAtValue = storage && storage.getItem(CEP_SAVED_AT_KEY);
        const savedAt = savedAtValue == null ? null : Number(savedAtValue);
        const expired = savedAt !== null && (!Number.isFinite(savedAt) || savedAt <= 0 || Date.now() - savedAt > CEP_RETENTION_MS || savedAt > Date.now());
        if (!isCepFormat(value) || expired) {
          sessionCep = '';
          if (expired) clearPersistedCep(documentRoot, windowRoot);
        } else {
          sessionCep = formatCep(value);
        }
      } catch { return; }
      if (typeof windowRoot.dispatchEvent === 'function' && typeof windowRoot.CustomEvent === 'function') {
        windowRoot.dispatchEvent(new windowRoot.CustomEvent('maknorte:cep:changed', {
          detail: { cep: sessionCep, verification: sessionCep ? 'format_only' : 'not_provided', source: 'storage' }
        }));
      }
    });

    renderBanner(documentRoot, windowRoot);
    injectCards(documentRoot);

    if (windowRoot.MutationObserver) {
      const observer = new windowRoot.MutationObserver(() => injectCards(documentRoot));
      observer.observe(documentRoot.body, { childList: true, subtree: true });
      windowRoot.setTimeout(() => observer.disconnect(), 60000);
    }
  }

  return {
    CEP_KEY,
    checkoutNotice,
    cleanCep,
    focusInlineInput,
    formatCep,
    getCep,
    init,
    isCepFormat,
    readStoredCep,
    setCep,
    storeCep
  };
});
